import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import PlaceIcon from '@mui/icons-material/Place';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableHeadCell from 'components/TableHeadCell';
import TableCell from 'components/TableCell';
import FabDialog from 'components/FabDialog';

import { useAvoidSuspense, useDelete, useSkipFirstLoad } from 'lib';
import { useSearch } from 'components/Search';
import { usePagination } from 'components/Pagination';

import Query, {
  CountriesListRefetchQuery,
} from '__generated__/CountriesListRefetchQuery.graphql';
import { CountriesList_data$key } from '__generated__/CountriesList_data.graphql';
import CountriesListRow from 'components/CountriesListRow';
import CountriesListAdd from 'components/CountriesListAdd';

type Props = {
  data: CountriesList_data$key;
};

const CountriesList: FC<Props> = ({ data }) => {
  const { search } = useSearch();
  const { variables } = usePagination();

  const [{ countries }, { refetch, loading }] = useAvoidSuspense<
    CountriesListRefetchQuery,
    CountriesList_data$key
  >(
    graphql`
      fragment CountriesList_data on Query
      @refetchable(queryName: "CountriesListRefetchQuery") {
        countries(first: $first, after: $after, search: $search) {
          __id
          edges {
            node {
              id

              ...CountriesListRow_country
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    data,
    Query
  );

  const connectionId = countries?.__id;

  const [deleteCountry, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = countries ?? {};

  useSkipFirstLoad(
    useCallback(() => {
      refetch({
        ...variables,
        search,
      });
    }, [refetch, search, variables])
  );

  return (
    <>
      <Table
        search
        pagination
        pageInfo={pageInfo ?? null}
        icon={<PlaceIcon />}
        loading={loading || deleting}
        head={
          <TableRow>
            <TableHeadCell name="name">Pavadinimas</TableHeadCell>

            <TableCell />
          </TableRow>
        }>
        {edges?.map(({ node }) => (
          <CountriesListRow
            key={node.id}
            country={node}
            onDelete={deleteCountry}
          />
        ))}
      </Table>

      {connectionId && (
        <FabDialog fullWidth maxWidth="sm" icon={<AddLocationIcon />}>
          <CountriesListAdd connectionId={connectionId} />
        </FabDialog>
      )}
    </>
  );
};

export default CountriesList;
